import { lazy } from 'react';
import { Navigate } from "react-router-dom";

import { ACCESS_RIGHTS } from "../constants/accessRights";

// =====================================================
// #region AUTHENTICATION INNER PAGES
// =====================================================
const ResetPassword = lazy(() => import("../pages/Authentication/ResetPassword"));
const Alt404 = lazy(() => import("../pages/AuthenticationInner/Errors/Alt404"));
const Basic404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500"));
const LockScreen = lazy(() => import("../pages/Authentication/LockScreen"));
const Offlinepage = lazy(() => import("../pages/AuthenticationInner/Errors/Offlinepage"));
const ExpiredToken = lazy(() => import("../pages/Authentication/ExpiredToken"));
// #endregion

// =====================================================
// #region GENERAL
// =====================================================
const ComingSoon = lazy(() => import("../pages/Others/ComingSoon/ComingSoon"));
const Maintenance = lazy(() => import("../pages/Others/Maintenance/Maintenance"));
const NotFound = lazy(() => import("../pages/Others/NotFound/NotFound"));
// #endregion

// =====================================================
// #region AUTHENTICATION
// =====================================================
const Login = lazy(() => import("../pages/Authentication/Login"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
const ForgetPassword = lazy(() => import("../pages/Authentication/ForgetPassword"));;
const Register = lazy(() => import("../pages/Authentication/Register"));;
// #endregion

// =====================================================
// #region CORE
// =====================================================
const Security_AuditLogs = lazy(() => import("../pages/Security/AuditLogs/index"));
const Security_Roles = lazy(() => import("../pages/Security/Roles/index"));
const Security_Users = lazy(() => import("../pages/Security/Users/index"));
const Security_AccessRequests = lazy(() => import("../pages/Security/AccessRequests/index"));
// #endregion

// =====================================================
// #region NATIONAL ACADEMY OF SPORTS
// =====================================================
const NAS_Dashboard = lazy(() => import("../pages/NAS/Dashboard/index"));
const NAS_Students = lazy(() => import("../pages/NAS/Students/index"));
const NAS_ActiveStudents = lazy(() => import("../pages/NAS/ActiveStudents/index"));
const NAS_InActiveStudents = lazy(() => import("../pages/NAS/InActiveStudents/index"));
const NAS_StudentLocations = lazy(() => import("../pages/NAS/StudentLocations/index"));

const NAS_CheckIns = lazy(() => import("../pages/NAS/CheckIns/index"));
const NAS_ClinicVisits = lazy(() => import("../pages/NAS/ClinicVisits/index"));
const NAS_ClinicVisits_ViewAll = lazy(() => import("../pages/NAS/ClinicVisits/Components/ViewAll"));
const NAS_ClinicVisits_ViewToday = lazy(() => import("../pages/NAS/ClinicVisits/Components/ViewToday"));
const NAS_ClinicVisits_ViewForFollowupToday = lazy(() => import("../pages/NAS/ClinicVisits/Components/ViewForFollowupToday"));

const NAS_GuidanceVisits = lazy(() => import("../pages/NAS/GuidanceVisits/index"));
const NAS_GUIDANCE_COUNSELOR_VISITS_ViewAll = lazy(() => import("../pages/NAS/GuidanceVisits/Components/ViewAll"));
const NAS_GUIDANCE_COUNSELOR_VISITS_ViewToday = lazy(() => import("../pages/NAS/GuidanceVisits/Components/ViewToday"));
const NAS_GUIDANCE_COUNSELOR_VISITS_ViewForFollowupToday = lazy(() => import("../pages/NAS/GuidanceVisits/Components/ViewForFollowupToday"));

const NAS_Dormitory = lazy(() => import("../pages/NAS/Dormitory/index"));
const NAS_Dormitory_ViewAll = lazy(() => import("../pages/NAS/Dormitory/Components/ViewAll"));
const NAS_Dormitory_DormitoryRoom = lazy(() => import("../pages/NAS/Dormitory/Components/DormitoryRoom"));
const NAS_Dormitory_HomeLeave = lazy(() => import("../pages/NAS/Dormitory/Components/HomeLeave"));
const NAS_Dormitory_Competition = lazy(() => import("../pages/NAS/Dormitory/Components/Competition"));

const NAS_Settings = lazy(() => import("../pages/NAS/Settings/index"));
const NAS_Reports = lazy(() => import("../pages/NAS/Reports/index"));

const NAS_Settings_Buildings = lazy(() => import("../pages/NAS/Settings/BuildingLocation"));
const NAS_Settings_Clinic_Visit_Types = lazy(() => import("../pages/NAS/Settings/ClinicVisitTypes"));
const NAS_Settings_Clock_In_Device = lazy(() => import("../pages/NAS/Settings/ClockInDevice"));
const NAS_Settings_Dormitory_Rooms = lazy(() => import("../pages/NAS/Settings/DormitoryRooms"));
const NAS_Settings_Grade_Levels = lazy(() => import("../pages/NAS/Settings/GradeLevels"));
const NAS_Settings_Sections = lazy(() => import("../pages/NAS/Settings/Sections"));
const NAS_Settings_Guidance_Counsel_Session_Types = lazy(() => import("../pages/NAS/Settings/GuidanceSessionTypes"));
const NAS_Settings_Guidance_Counsel_Visit_Types = lazy(() => import("../pages/NAS/Settings/GuidanceVisitTypes"));
const NAS_Settings_RFID_Cards = lazy(() => import("../pages/NAS/Settings/RFIDCards"));
const NAS_Settings_Sports = lazy(() => import("../pages/NAS/Settings/Sports"));
// #endregion

// =====================================================
// #region SOCIALS
// =====================================================
const Socials_FAQ = lazy(() => import("../pages/Socials/FAQ/index"));
const Socials_PrivacyPolicy = lazy(() => import("../pages/Socials/PrivacyPolicy/index"));
const Socials_TermsAndConditions = lazy(() => import("../pages/Socials/TermsAndConditions/index"));
// #endregion

const authProtectedRoutes = [
  { path: "/index", component: <NAS_Dashboard /> },

  // =====================================================
  // #region CORE
  // =====================================================
  {
    path: "/security/users",
    component: <Security_Users />,
    permissionTypeId: [
      ACCESS_RIGHTS.CORE_SECURITY_USERS,
    ]
  },
  {
    path: "/security/roles",
    component: <Security_Roles />,
    permissionTypeId: [
      ACCESS_RIGHTS.CORE_SECURITY_ROLES,
    ]
  },
  {
    path: "/security/auditLogs",
    component: <Security_AuditLogs />,
    permissionTypeId: [
      ACCESS_RIGHTS.CORE_SECURITY_AUDIT_LOGS,
    ]
  },
  {
    path: "/security/accessrequests",
    component: <Security_AccessRequests />,
    permissionTypeId: [
      // ACCESS_RIGHTS.CORE_SECURITY_AUDIT_LOGS,
    ]
  },
  // #endregion

  // =====================================================
  // #region NATIONAL ACADEMY OF SPORTS
  // =====================================================
  {
    path: "/nas/dashboard",
    component: <NAS_Dashboard />
  },
  {
    path: "/nas/students",
    component: <NAS_Students />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STUDENTS
    ]
  },
  {
    path: "/nas/active-students",
    component: <NAS_ActiveStudents />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STUDENTS
    ]
  },
  {
    path: "/nas/inactive-students",
    component: <NAS_InActiveStudents />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STUDENTS
    ]
  },
  {
    path: "/nas/student-locations",
    component: <NAS_StudentLocations />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STUDENTS
    ]
  },
  {
    path: "/nas/checkins",
    component: <NAS_CheckIns />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_CHECK_INS
    ]
  },
  {
    path: "/nas/guidanceVisits",
    component: <NAS_GuidanceVisits />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_GUIDANCE_COUNSELOR_VISITS
    ]
  },
  {
    path: "/nas/clinicvisits",
    component: <NAS_ClinicVisits />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_CLINIC_VISITS
    ]
  },
  {
    path: "/nas/clinicvisits/viewall",
    component: <NAS_ClinicVisits_ViewAll />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_CLINIC_VISITS
    ]
  },
  {
    path: "/nas/clinicvisits/viewToday",
    component: <NAS_ClinicVisits_ViewToday />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_CLINIC_VISITS
    ]
  },
  {
    path: "/nas/clinicvisits/viewForFollowupToday",
    component: <NAS_ClinicVisits_ViewForFollowupToday />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_CLINIC_VISITS
    ]
  },
  {
    path: "/nas/guidanceVisits/viewall",
    component: <NAS_GUIDANCE_COUNSELOR_VISITS_ViewAll />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_GUIDANCE_COUNSELOR_VISITS
    ]
  },
  {
    path: "/nas/guidanceVisits/viewToday",
    component: <NAS_GUIDANCE_COUNSELOR_VISITS_ViewToday />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_GUIDANCE_COUNSELOR_VISITS
    ]
  },
  {
    path: "/nas/guidanceVisits/viewForFollowupToday",
    component: <NAS_GUIDANCE_COUNSELOR_VISITS_ViewForFollowupToday />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_GUIDANCE_COUNSELOR_VISITS
    ]
  },
  {
    path: "/nas/dormitory",
    component: <NAS_Dormitory />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_DORMITORY_VISITS
    ]
  },
    {
    path: "/nas/dormitory/dormitoryRoom",
    component: <NAS_Dormitory_DormitoryRoom />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_DORMITORY_VISITS
    ]
  },
  ,
    {
    path: "/nas/dormitory/homeLeave",
    component: <NAS_Dormitory_HomeLeave />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_DORMITORY_VISITS
    ]
  },
  ,
    {
    path: "/nas/dormitory/competition",
    component: <NAS_Dormitory_Competition />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_DORMITORY_VISITS
    ]
  },
  {
    path: "/nas/dormitory/viewall",
    component: <NAS_Dormitory_ViewAll />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_TRANSACTIONS_DORMITORY_VISITS
    ]
  },
  {
    path: "/nas/settings",
    component: <NAS_Settings />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_BUILDING_LOCATIONS,
      ACCESS_RIGHTS.NAS_STATICDATA_CLINIC_VISIT_TYPES,
      ACCESS_RIGHTS.NAS_STATICDATA_CLOCK_IN_DEVICES,
      ACCESS_RIGHTS.NAS_STATICDATA_ROOMS,
      ACCESS_RIGHTS.NAS_STATICDATA_GRADE_LEVELS,
      ACCESS_RIGHTS.NAS_STATICDATA_GUIDANCE_COUNSELOR_SESSION_TYPES,
      ACCESS_RIGHTS.NAS_STATICDATA_RFID_CARDS,
      ACCESS_RIGHTS.NAS_STATICDATA_SPORTS,
    ]
  },

  {
    path: "/nas/settings/buildings",
    component: <NAS_Settings_Buildings />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_BUILDING_LOCATIONS
    ]
  },
  {
    path: "/nas/settings/clinic-visit-types",
    component: <NAS_Settings_Clinic_Visit_Types />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_CLINIC_VISIT_TYPES
    ]
  },
  {
    path: "/nas/settings/clock-in-devices",
    component: <NAS_Settings_Clock_In_Device />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_CLOCK_IN_DEVICES
    ]
  },
  {
    path: "/nas/settings/dormitory-rooms",
    component: <NAS_Settings_Dormitory_Rooms />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_ROOMS
    ]
  },
  {
    path: "/nas/settings/grade-levels",
    component: <NAS_Settings_Grade_Levels />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_GRADE_LEVELS
    ]
  },
  {
    path: "/nas/settings/sections",
    component: <NAS_Settings_Sections />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_SECTIONS
    ]
  },
  {
    path: "/nas/settings/guidance-counselor-session-types",
    component: <NAS_Settings_Guidance_Counsel_Session_Types />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_GUIDANCE_COUNSELOR_SESSION_TYPES
    ]
  },
  {
    path: "/nas/settings/guidance-counselor-visit-types",
    component: <NAS_Settings_Guidance_Counsel_Visit_Types />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_GUIDANCEVISITTYPES
    ]
  },
  {
    path: "/nas/settings/rfid-cards",
    component: <NAS_Settings_RFID_Cards />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_RFID_CARDS
    ]
  },
  {
    path: "/nas/settings/sports",
    component: <NAS_Settings_Sports />,
    permissionTypeId: [
      ACCESS_RIGHTS.NAS_STATICDATA_SPORTS
    ]
  },
  {
    path: "/nas/reports",
    component: <NAS_Reports />,
    permissionTypeId: [
    ]
  },
  // #endregion

  // =====================================================
  // #region OTHERS
  // =====================================================
  { path: "/socials/faq", component: <Socials_FAQ /> },
  { path: "/socials/terms-conditions", component: <Socials_TermsAndConditions />, },
  { path: "/socials/privacy-policy", component: <Socials_PrivacyPolicy /> },

  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  { path: "/", exact: true, component: <Navigate to="/nas/dashboard" />, },
  { path: "*", component: <Navigate to="/notfound" /> },
  // #endregion
];

const publicRoutes = [
  // Authentication Page
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <Login /> },
  { path: "/forgot-password", component: <ForgetPassword /> },
  { path: "/register", component: <Register /> },
  { path: "/lockscreen", component: <LockScreen /> },
  { path: "/notfound", component: <NotFound /> },
  { path: "/reset-password", component: <ResetPassword /> },
  { path: "/expired-token", component: <ExpiredToken /> },

  //AuthenticationInner pages
  { path: "/auth-404-basic", component: <Basic404 /> },
  { path: "/auth-404-alt", component: <Alt404 /> },
  { path: "/auth-500", component: <Error500 /> },
  { path: "/pages-maintenance", component: <Maintenance /> },
  { path: "/pages-coming-soon", component: <ComingSoon /> },
  { path: "/auth-offline", component: <Offlinepage /> },

];

export { authProtectedRoutes, publicRoutes };

